/**
 * light_pollution — how much ARTIFICIAL light the settlements put into the night sky over a point, and from which
 * directions (the night-sky realism wave, 2026-10-06). PURE (no three, no DOM): the one model the sky shader's light
 * domes, the stars' visibility, the city-light carpets, the haze and the clouds' undersides all read, so a town looks the
 * same from every system that shows it.
 *
 * THE MODEL. Each settlement is a light SOURCE with a centre strength `A` and a core radius `r` (km): its contribution to
 * the ZENITH sky brightness at a horizontal distance d (km), as a ratio of the natural night sky, is
 *
 *     R(d) = A · (1 + (d / r)²)^(−1.25)
 *
 * — flat inside the lit core, Walker's law far away (R ∝ d^−2.5: geometry's d^−2 steepened by the air's extinction and
 * forward scattering; Walker 1977; Garstang 1986), faded out past ~60 km (the Earth's curvature and terrain hide a dome
 * beyond Walker's 50–100 km validity). Contributions add, then saturate toward an inner-city ceiling (`R_MAX`).
 * A and r are CALIBRATED per tier to sky-quality targets, not to Atlas's census (a 90-resident "city" is drawn like a
 * real town of tens of thousands): a capital's centre reads ~17 mag/arcsec² (Bortle 9), a city's ~18.3 (Bortle 8), a
 * town's ~19.6 (Bortle 5–6), a hamlet's ~21 (Bortle 4); 3 km from a city the zenith is ~20.7 (Bortle 4–5). The natural
 * sky is 22.0 mag/arcsec² (Bortle 1–2 — airglow + starlight + zodiacal light, ≈ 1.7·10⁻⁴ cd/m²).
 *
 * WHAT IT ANSWERS. `skyglowAt` — the zenith ratio, its sky-quality magnitude (SQM), the Bortle class, the naked-eye
 * limiting magnitude (Schaefer / Crumey's NELM = 7.93 − 5·log10(10^(4.316 − SQM/5) + 1)) and the glow's colour.
 * `domesAt` — the K strongest LIGHT DOMES over the horizon: each a city's bearing, its brightness at the horizon (a
 * dome is many times brighter low down than at the zenith — the light's long path through the lit air), its azimuthal
 * half-width and its elevation e-folding angle (`domeRadiance` evaluates one along a view direction; the sky shader and
 * the CPU sky sampler use the same arithmetic). Inside a city the dome becomes the whole sky (`allSky` in the result).
 *
 * COLOUR. Settler-tech light is warm: amber street light (~2200–2700 K, the sodium look) with the windows' paler mix.
 * Aurelian towns glow a touch warmer, Borean a touch whiter (`CIV_TINT`) — a cue, never a theme colour. The sky's
 * natural night is blue-grey; skyglow is what turns a city's sky orange-brown (the reference photos: Stanley's
 * country-vs-city pair; the APOD 2020-04-08 Slovakia panorama; Soltesz's Flagstaff dome).
 *
 * Sources: Walker, "The effects of urban lighting on the brightness of the night sky", PASP 89 (1977); Garstang, "Model
 * for artificial night-sky illumination", PASP 98 (1986); Cinzano et al., "The first world atlas of the artificial night
 * sky brightness", MNRAS 328 (2001); Bortle, Sky & Telescope (2001); Schaefer, "Astronomy and the limits of vision"
 * (1993); Crumey, "Human contrast threshold and astronomical visibility", MNRAS 442 (2014).
 */

export type GlowTier = 'city' | 'town' | 'hamlet' | 'capital' | 'district' | 'circuit' | 'player';

/** The natural night sky's zenith brightness, mag/arcsec² (a moonless, unpolluted site). */
export const NATURAL_SQM = 22.0;
/** The brightest a sky gets over a lit city (ratio to natural; ≈ 16.8 mag/arcsec²). */
export const R_MAX = 120;
/** Past this distance (km) a source is under the horizon / behind terrain: it fades to 0 over the last 25 %. */
export const SOURCE_MAX_KM = 60;

/** Per-tier centre strength `A` (zenith ratio) and core radius `r` (km). Districts are the capital's (0 here). */
export const TIER_GLOW: Record<GlowTier, { A: number; r: number }> = {
  capital: { A: 220, r: 3.2 },
  city: { A: 30, r: 1.3 }, // r 1.0 → 1.3 (2026-10-06): the far tail was a ~3k-person town's by Walker's law; a city's dome must read from 10–20 km
  town: { A: 8, r: 0.7 }, // r 0.5 → 0.7 (2026-10-06): the same — a town's dome was gone by 4 km
  hamlet: { A: 1.5, r: 0.25 },
  circuit: { A: 3, r: 0.6 },
  district: { A: 0, r: 0.5 },
  player: { A: 4, r: 0.4 },
};

/** Linear-RGB tints of the glow (luminance ≈ 1). Warm amber; Aurelian warmer, Borean whiter. */
export const GLOW_TINT: [number, number, number] = [1.32, 0.94, 0.55];
export const CIV_TINT: Record<'a' | 'b' | '', [number, number, number]> = {
  a: [1.40, 0.92, 0.47],
  b: [1.14, 0.98, 0.80],
  '': GLOW_TINT,
};

export interface LightSource {
  /** Stable id (the settlement id, or `p<cityId>` for a player city). */
  id: string;
  /** Centre, metres (world XZ, absolute). */
  x: number; z: number;
  /** Centre strength (zenith ratio at the centre) and core radius (km). */
  A: number; r: number;
  tint: [number, number, number];
}

/**
 * A settlement as this model needs it (the client's `SettlementSite` fits; `civ` optional). `aScale` / `rScale` (OPTIONAL,
 * agent P 2026-10-06) scale the tier's centre strength A and core radius r for THIS source — a player city is sized by its
 * rank and what it has built (`playerCityGlowSite`); absent = 1, so every settlement is exactly what it was.
 */
export interface GlowSite { id: number | string; centerX: number; centerZ: number; tier: string; civ?: 'a' | 'b' | ''; aScale?: number; rScale?: number }

/** The light sources for a site list (districts skipped — the capital is one source). */
export function lightSourcesFor(sites: ReadonlyArray<GlowSite>, scale = 1): LightSource[] {
  const out: LightSource[] = [];
  for (const s of sites) {
    const g = TIER_GLOW[(s.tier as GlowTier)] ?? null;
    if (!g || g.A <= 0) continue;
    const aS = s.aScale ?? 1, rS = s.rScale ?? 1;
    // A zero, negative or non-finite per-source scale is no source (a dark city) — never a NaN or an infinity in the sky.
    if (!(aS > 0 && aS < 1e3) || !(rS > 0 && rS < 1e3)) continue;
    out.push({ id: String(s.id), x: s.centerX, z: s.centerZ, A: g.A * scale * aS, r: g.r * rS, tint: tintOfCiv(s.civ) });
  }
  return out;
}

/** A source's glow tint by civilisation ('' or anything else = the plain settler glow). */
export function tintOfCiv(civ?: string | null): [number, number, number] {
  return CIV_TINT[(civ ?? '') as 'a' | 'b' | ''] ?? GLOW_TINT;
}

// ── PLAYER CITIES (agent P, 2026-10-06: "player cities get no city lights yet") ──────────────────────────────────────
/**
 * A player-founded city (a `city` row) is a `player` source, id `p<cityId>`, sized by what it is:
 *
 *   STRENGTH  A = TIER_GLOW.player.A × residents(rank) / PLAYER_GLOW_REF_RESIDENTS × build
 *     residents(rank) is the rank's floor (the server's CITY_RANK_MIN_CITIZENS: 5 / 10 / 15 / 30 / 40). WHY: a town's
 *     artificial zenith brightness scales with its population (Walker 1977 — L ∝ P), and the contract's `player`
 *     constant A 4 stands for a Village (10). At a full build: 2 / 4 / 6 / 12 / 16 — an Outpost a little over a
 *     hamlet (1.5), a Metropolis between a town (8) and a city (30).
 *   BUILD = PLAYER_BUILD_FLOOR + (1 − floor) · min(1, lit / expected(rank)). WHY: half of a town's upward light follows
 *     its people whatever stands; the other half is the lit fabric it has actually BUILT — its lit buildings, floodlit
 *     monuments and lamps (`lit`, counted by city_night_lights' PLAYER_STRUCTURE_LIGHT) against a building per two
 *     residents plus the hall and a lamp (expected 4.5 / 7 / 9.5 / 17 / 22). A freshly ranked Metropolis of five
 *     buildings glows like a town; a built-out one between a town and a city.
 *   CORE  r = TIER_GLOW.player.r × radiusM / PLAYER_GLOW_REF_RADIUS_M. WHY: the contract's r 0.4 km is a Village's 200 m
 *     border, and the lit core grows with the city's border (its radius of influence grows with rank): 0.30 / 0.40 /
 *     0.60 / 0.80 / 0.90 km (a hamlet 0.25, a town 0.7, a city 1.3). The far tail (∝ A·r^2.5) lands an Outpost at twice
 *     a hamlet's and a Metropolis between a town's and a city's.
 *
 * Only `active` and `provisional` cities count (a forming city glows); an `invalid` one is dark. The tint follows the
 * city's allegiance (`city_allegiance.civ`): 'a' / 'b' the civ tint, '' or a player civilisation (`p:<civId>`) the
 * plain glow.
 */
export const PLAYER_RANK_RESIDENTS = [5, 10, 15, 30, 40] as const; // ≡ server/src/lib/constants.ts CITY_RANK_MIN_CITIZENS (pinned)
export const PLAYER_RANK_RADIUS_M = [150, 200, 300, 400, 450] as const; // ≡ server/src/lib/constants.ts CITY_RANK_RADIUS_M (pinned)
/** The residents and the border the contract's `player` constants (A 4, r 0.4 km) stand for: a Village. */
export const PLAYER_GLOW_REF_RESIDENTS = 10;
export const PLAYER_GLOW_REF_RADIUS_M = 200;
/** The share of a player city's light that follows its people whatever it has built. */
export const PLAYER_BUILD_FLOOR = 0.5;

/** Does a city of this status give light? (`active` | `provisional`; `invalid` and anything unknown are dark.) */
export function playerCityLive(status: string | null | undefined): boolean { return status === 'active' || status === 'provisional'; }
function rankIndex(rank: number): number { return Math.max(0, Math.min(PLAYER_RANK_RESIDENTS.length - 1, (Math.round(Number(rank)) || 1) - 1)); }
/** The lit structures a city of `rank` is expected to stand: a building per two residents, its hall and a lamp. */
export function playerExpectedLit(rank: number): number { return PLAYER_RANK_RESIDENTS[rankIndex(rank)] / 2 + 2; }
/** The glow scales of a player city (see the block above). `radiusM` is the row's (≤ 0 or absent = the rank's). */
export function playerCityGlow(rank: number, radiusM: number | null | undefined, lit: number): { aScale: number; rScale: number; build: number } {
  const i = rankIndex(rank);
  const rad = Number.isFinite(radiusM) && (radiusM as number) > 0 ? (radiusM as number) : PLAYER_RANK_RADIUS_M[i];
  const fill = Math.max(0, Math.min(1, (Number.isFinite(lit) ? Math.max(0, lit) : 0) / playerExpectedLit(rank)));
  const build = PLAYER_BUILD_FLOOR + (1 - PLAYER_BUILD_FLOOR) * fill;
  return { aScale: (PLAYER_RANK_RESIDENTS[i] / PLAYER_GLOW_REF_RESIDENTS) * build, rScale: rad / PLAYER_GLOW_REF_RADIUS_M, build };
}
/** A city's allegiance as a glow civ: 'a' / 'b', else '' (neutral and a player civilisation `p:<civId>` take the plain glow). */
export function glowCivOfAlignment(alignment: string | null | undefined): 'a' | 'b' | '' { return alignment === 'a' || alignment === 'b' ? alignment : ''; }
/** The player city as a contract site (`p<cityId>`, tier `player`), or null for a dark (invalid) city. */
export function playerCityGlowSite(
  city: { cityId: number | bigint | string; centerX: number; centerZ: number; cityRank: number; radiusM?: number | null; status: string },
  alignment: string | null | undefined, lit: number,
): GlowSite | null {
  if (!playerCityLive(city.status) || !Number.isFinite(city.centerX) || !Number.isFinite(city.centerZ)) return null;
  const g = playerCityGlow(city.cityRank, city.radiusM, lit);
  return { id: `p${String(city.cityId)}`, centerX: city.centerX, centerZ: city.centerZ, tier: 'player', civ: glowCivOfAlignment(alignment), aScale: g.aScale, rScale: g.rScale };
}

/** One source's zenith ratio at horizontal distance `dKm` (Plummer core, Walker's d^−2.5 tail, the horizon cut). */
export function sourceRatio(src: Pick<LightSource, 'A' | 'r'>, dKm: number): number {
  if (!(dKm >= 0) || !(src.A > 0)) return 0;
  const q = dKm / src.r;
  let v = src.A * Math.pow(1 + q * q, -1.25);
  const cut0 = SOURCE_MAX_KM * 0.75;
  if (dKm > cut0) v *= Math.max(0, 1 - (dKm - cut0) / (SOURCE_MAX_KM - cut0));
  return v;
}

/** Soft saturation toward R_MAX (a sky cannot pass inner-city bright however many cities stack). */
export function saturateRatio(R: number): number { return R <= 0 ? 0 : R / (1 + R / R_MAX); }

export function sqmFromRatio(R: number): number { return NATURAL_SQM - 2.5 * Math.log10(1 + Math.max(0, R)); }
/** Luminance (cd/m²) of a sky of `sqm` mag/arcsec² (10.8·10⁴ · 10^(−0.4 m)). */
export function sqmToNits(sqm: number): number { return 10.8e4 * Math.pow(10, -0.4 * sqm); }
/** Naked-eye limiting magnitude for a sky of `sqm` (Schaefer / Crumey fit; 6.6 at 22.0, ≈ 4.0 at 18.0). */
export function nelmFromSqm(sqm: number): number { return 7.93 - 5 * Math.log10(Math.pow(10, 4.316 - sqm / 5) + 1); }
/** Bortle class (1–9) from SQM — the common retrofit table (Bortle's article has no numbers; a guide, not a law). */
export function bortleFromSqm(sqm: number): number {
  if (sqm >= 21.99) return 1;
  if (sqm >= 21.89) return 2;
  if (sqm >= 21.69) return 3;
  if (sqm >= 20.49) return 4;
  if (sqm >= 19.5) return 5;
  if (sqm >= 18.94) return 6;
  if (sqm >= 18.38) return 7;
  if (sqm >= 17.8) return 8;
  return 9;
}

export interface Skyglow {
  /** Artificial zenith brightness as a ratio of the natural sky (0 = pristine). */
  ratio: number;
  sqm: number; bortle: number; nelm: number;
  /** The glow's colour (ratio-weighted mean of the sources' tints; luminance ≈ 1). */
  tint: [number, number, number];
}

/** The artificial skyglow over a point (metres, absolute XZ). */
export function skyglowAt(x: number, z: number, sources: ReadonlyArray<LightSource>): Skyglow {
  let R = 0, tr = 0, tg = 0, tb = 0;
  for (const s of sources) {
    const d = Math.hypot(s.x - x, s.z - z) / 1000;
    if (d > SOURCE_MAX_KM) continue;
    const v = sourceRatio(s, d);
    if (v <= 0) continue;
    R += v; tr += v * s.tint[0]; tg += v * s.tint[1]; tb += v * s.tint[2];
  }
  const ratio = saturateRatio(R);
  const tint: [number, number, number] = R > 0 ? [tr / R, tg / R, tb / R] : [...GLOW_TINT];
  const sqm = sqmFromRatio(ratio);
  return { ratio, sqm, bortle: bortleFromSqm(sqm), nelm: nelmFromSqm(sqm), tint };
}

export interface LightDome {
  id: string;
  /** Bearing from the viewer, radians, atan2(dx, dz) (the world map / vehicle convention: 0 = +Z). */
  az: number;
  /** Unit horizontal direction toward the source (x, z). */
  dirX: number; dirZ: number;
  /** Distance, km. */
  dist: number;
  /** The dome's brightness at the horizon toward the source, as a ratio of the natural sky. */
  peak: number;
  /** Azimuthal half-width (radians, the Gaussian σ) and the elevation e-folding angle (radians). */
  halfWidth: number; height: number;
  tint: [number, number, number];
}

/**
 * The dome's geometry for a source at `dKm`. Peak: the dome's EXCESS over the zenith glow at the horizon toward the
 * source — the source's own zenith ratio × a low-angle gain that grows with distance (a far dome is a bright band on the
 * horizon over a barely changed zenith; inside the city the whole sky is lit and the excess is small — the uniform glow
 * carries it). Width: the city's angular half-size plus the scattering spread. Height: the glowing air reaches ~3 km
 * over the city — its elevation angle seen from `dKm`, never under 4°.
 */
/** THE FAR DOME's extra horizon gain and the distance it builds over (km) past a source's 4 core radii. */
export const DOME_FAR_GAIN = 20;
export const DOME_FAR_KM = 25;
export function domeShape(src: Pick<LightSource, 'A' | 'r'>, dKm: number): { peak: number; halfWidth: number; height: number } {
  const z = sourceRatio(src, dKm);
  // THE FAR DOME (2026-10-06): past the town's edge (4 r) the horizon keeps gaining on the zenith — the line of sight runs
  // through more of the lit air while the zenith term falls as d^−2.5 (all-sky maps from 20–40 km read a dome tens of
  // times the zenith excess). +20 over the next 25 km; the near field (the calibrated anchors) is unchanged.
  const gain = 1 + 10 * Math.min(1, dKm / Math.max(0.5, src.r * 4)) + DOME_FAR_GAIN * Math.min(1, Math.max(0, dKm - src.r * 4) / DOME_FAR_KM);
  const peak = z * gain;
  const halfWidth = Math.min(Math.PI, Math.atan2(src.r * 1.4, Math.max(dKm, 1e-3)) + 0.22);
  const height = Math.max(0.07, Math.atan2(3, Math.max(dKm, 1e-3)));
  return { peak, halfWidth, height };
}

/** The K strongest domes over a point (strongest first). Sources closer than their core radius are still listed. */
export function domesAt(x: number, z: number, sources: ReadonlyArray<LightSource>, k = 4): LightDome[] {
  const all: LightDome[] = [];
  for (const s of sources) {
    const dx = s.x - x, dz = s.z - z;
    const dist = Math.hypot(dx, dz) / 1000;
    if (dist > SOURCE_MAX_KM) continue;
    const sh = domeShape(s, dist);
    if (sh.peak <= 1e-4) continue;
    const inv = dist > 1e-6 ? 1 / (dist * 1000) : 0;
    all.push({ id: s.id, az: Math.atan2(dx, dz), dirX: dx * inv, dirZ: dz * inv, dist, ...sh, tint: s.tint });
  }
  all.sort((a, b) => b.peak - a.peak);
  return all.slice(0, k);
}

/**
 * One dome's EXCESS radiance ratio along a view direction (unit, y up): peaked toward its bearing, falling with
 * elevation to exactly 0 at the zenith (the zenith belongs to the uniform glow — no double count). Below the horizon it
 * holds its horizon value (the ground hides it — the caller masks). A source the viewer stands inside (horizontal
 * direction 0) lights every bearing equally. The sky shader is this arithmetic in TSL.
 */
export function domeRadiance(dome: Pick<LightDome, 'dirX' | 'dirZ' | 'peak' | 'halfWidth' | 'height'>, vx: number, vy: number, vz: number): number {
  const h = Math.hypot(vx, vz);
  const elev = Math.atan2(Math.max(vy, 0), Math.max(h, 1e-6));
  let azW = 1;
  if (dome.dirX !== 0 || dome.dirZ !== 0) {
    const c = h > 1e-6 ? (vx * dome.dirX + vz * dome.dirZ) / h : 1;
    const dAz = Math.acos(Math.max(-1, Math.min(1, c)));
    azW = Math.exp(-0.5 * (dAz / dome.halfWidth) ** 2);
  }
  const top = Math.exp(-(Math.PI / 2) / dome.height);
  return dome.peak * azW * Math.max(0, (Math.exp(-elev / dome.height) - top) / (1 - top));
}

/** The whole artificial sky along a view direction, as a ratio of the natural sky: the zenith glow + every dome. */
export function artificialSkyRatio(glow: Skyglow, domes: ReadonlyArray<LightDome>, vx: number, vy: number, vz: number): number {
  // The uniform part brightens toward the horizon like any scattered sky: ×2.2 at the horizon, ×1 at the zenith.
  const sinh = Math.max(0, vy);
  let L = glow.ratio * (1 + 1.2 * (1 - sinh) * (1 - sinh));
  for (const d of domes) L += domeRadiance(d, vx, vy, vz);
  return Math.min(L, R_MAX * 2.5);
}

/**
 * THE MESOPIC STREET (the coordinator, 2026-10-06): the luminance (cd/m²) an observer's eye adapts to under a town's
 * own light — the lit ground, facades and windows around them, not the sky. A city centre's streets are ~1 cd/m²
 * (EN 13201's M classes span 0.3–2), a town's a few tenths, a hamlet's a few hundredths; the CIE mesopic range is
 * 0.005–5 cd/m². Read off the zenith glow (R ≈ 30 over a city centre → 1 cd/m², ~linear: both scale with the light
 * installed nearby), capped at 3 cd/m² (photopic — no rods). Its job: an eye in a lit town is MESOPIC, never fully
 * scotopic, so the Purkinje shift must not turn an amber city sky blue-grey (Kyba et al.: "humans near big cities do
 * not use scotopic vision to view the sky"). PostModule adds it to the adaptation luminance.
 */
export const STREET_NITS_PER_RATIO = 1 / 30;
export const STREET_NITS_MAX = 3;
export function streetAdaptationNits(zenithRatio: number): number {
  if (!(zenithRatio > 0)) return 0;
  return Math.min(STREET_NITS_MAX, zenithRatio * STREET_NITS_PER_RATIO);
}
